import React from "react";
import {
	FailureActionType,
	IVRFlowTypeEnum,
	TemplateType,
	WidgetTypeEnum,
} from "../../../common/enum";
import { IDropDown } from "../../../common/interfaces";
import {
	Box,
	FormControl,
	Grid,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	FormHelperText,
} from "../../../common/MaterialComponent";
import Shared from "../../../common/shared";
import AudioFileModel from "../../../models/RequestForHelpTemplates/AudioFileModel";
import WidgetLanguageDetailModel from "../../../models/RequestForHelpTemplates/WidgetLanguageDetailModel";
import { MultiLanguageWidget } from "../MultiLanguageWidget/MultiLanguageWidget";
import { MultiAudioUploadWidget } from "../MultiAudioUploadWidget/MultiAudioUploadWidget";
import FileUpload from "../../BatchNotification/FileUpload/FileUpload";
import { IsAudioConfigurationAllowed } from "../../../hooks/useIVRFlowWidgetConfiguration";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export interface FailureActionConfigurationProps {
	failureActionType: FailureActionType | string;
	failureDefaultValue?: string;
	failureMessage: string | WidgetLanguageDetailModel[];
	failureAudioFile: AudioFileModel | AudioFileModel[];
	selectedLanguageList: string[];
	partitionKey: string;
	rowKey: string;
	handleFailureActionConfigChange: (
		value: string,
		propertyName: string
	) => void;
	handleFailureLanguageConfigChange?: (
		obj: WidgetLanguageDetailModel,
		propertyName: string
	) => void;
	handleAudioFileChange: (obj: AudioFileModel, propertyName: string) => void;
	widgetType?: WidgetTypeEnum | string;
	ivrFlowType: IVRFlowTypeEnum | string;
	highlightRequiredDetail?: boolean;
}

export const FailureActionConfiguration: React.FC<FailureActionConfigurationProps> = ({
	failureActionType,
	failureDefaultValue,
	selectedLanguageList,
	failureMessage,
	failureAudioFile,
	partitionKey,
	rowKey,
	handleFailureActionConfigChange,
	handleAudioFileChange,
	handleFailureLanguageConfigChange,
	widgetType,
	ivrFlowType,
	highlightRequiredDetail,
}) => {
	const failureActionTypeList: IDropDown[] = Shared.convertEnumToList(
		FailureActionType
	);
	const strings = useGetComponentStringsContext().FailureActionConfiguration;

	return (
		<Grid item xs={12}>
			<FormControl fullWidth required>
				<InputLabel>{strings.failureActionTypeLabel}</InputLabel>
				<Select
					value={failureActionType}
					onChange={(event) => {
						handleFailureActionConfigChange(
							event.target.value as string,
							"failureActionType"
						);
					}}
					fullWidth
					error={!failureActionType && highlightRequiredDetail}
				>
					{failureActionTypeList.map((option: IDropDown) => (
						<MenuItem key={option.type} value={option.type}>
							<ListItemText primary={option.description} />
						</MenuItem>
					))}
				</Select>
				{!failureActionType && highlightRequiredDetail && (
					<FormHelperText error={!failureActionType && highlightRequiredDetail}>
						{strings.failureActionTypeErrorMessage}
					</FormHelperText>
				)}
			</FormControl>

			{failureActionType === FailureActionType.UseDefaultValue &&
				(widgetType === WidgetTypeEnum.PickLanguage ||
					selectedLanguageList.length > 0) && (
					<Box pt={1}>
						<FormControl fullWidth required>
							<InputLabel>{strings.failureDefaultValueLabel}</InputLabel>
							<Select
								value={failureDefaultValue}
								onChange={(event) => {
									handleFailureActionConfigChange(
										event.target.value as string,
										"failureDefaultValue"
									);
								}}
								fullWidth
								error={!failureDefaultValue && highlightRequiredDetail}
							>
								{selectedLanguageList.map((option: string) => (
									<MenuItem key={option} value={option}>
										<ListItemText primary={option} />
									</MenuItem>
								))}
							</Select>
							{!failureDefaultValue && highlightRequiredDetail && (
								<FormHelperText
									error={!failureDefaultValue && highlightRequiredDetail}
								>
									{strings.failureDefaultValueErrorMessage}
								</FormHelperText>
							)}
						</FormControl>
					</Box>
				)}
			{failureActionType === FailureActionType.UseDefaultValue &&
				widgetType !== WidgetTypeEnum.PickLanguage &&
				!selectedLanguageList.length && (
					<Box pt={1}>
						<TextField
							label={strings.failureDefaultValueLabel}
							value={failureDefaultValue}
							onChange={(event) => {
								handleFailureActionConfigChange(
									event.target.value as string,
									"failureDefaultValue"
								);
							}}
							fullWidth
							required
							error={!failureDefaultValue && highlightRequiredDetail}
							helperText={
								!failureDefaultValue && highlightRequiredDetail
									? strings.failureDefaultValueErrorMessage
									: ""
							}
						/>
					</Box>
				)}
			{failureActionType === FailureActionType.SendMessageAndExit &&
				widgetType === WidgetTypeEnum.PickLanguage && (
					<>
						<Box pt={1}>
							<Grid item xs={12}>
								<TextField
									label={strings.failureMessageTextLabel}
									value={failureMessage}
									onChange={(event) =>
										handleFailureActionConfigChange(
											event.target.value as string,
											"failureMessage"
										)
									}
									multiline
									rows={4}
									fullWidth
									required
									error={!failureMessage && highlightRequiredDetail}
									helperText={
										!failureMessage && highlightRequiredDetail
											? strings.failureMessageTextErrorMessage
											: ""
									}
								/>
							</Grid>
						</Box>
						{IsAudioConfigurationAllowed(ivrFlowType) && (
							<Box pt={2}>
								<Grid item xs={12}>
									<InputLabel>{strings.failureAudioFileLabel}</InputLabel>
									<Box mt={1} mb={2}>
										<FileUpload
											message={(failureAudioFile as AudioFileModel)!.value}
											index="PickLanguage_FailureAudioFile"
											audioDuration={
												(failureAudioFile as AudioFileModel)!.audioDuration
											}
											fileName={(failureAudioFile as AudioFileModel)!.fileName}
											removeUploadedFile={() =>
												handleAudioFileChange(
													new AudioFileModel(),
													"failureAudioFile"
												)
											}
											partitionKey={partitionKey}
											rowKey={rowKey}
											templateType={TemplateType.RequestForHelp}
											onFileChange={(
												fileUrl: string,
												fileName: string,
												audioDuration: number
											) => {
												handleAudioFileChange!(
													Object.assign(new AudioFileModel(), {
														value: fileUrl,
														fileName,
														audioDuration,
													}),
													"failureAudioFile"
												);
											}}
										></FileUpload>
									</Box>
								</Grid>
							</Box>
						)}
					</>
				)}
			{failureActionType === FailureActionType.SendMessageAndExit &&
				widgetType !== WidgetTypeEnum.PickLanguage && (
					<>
						<MultiLanguageWidget
							title={strings.failureMessageTextLabel}
							widgetLanguageDetail={
								failureMessage as WidgetLanguageDetailModel[]
							}
							selectedLanguageList={selectedLanguageList}
							onMessageDetailChange={(object: WidgetLanguageDetailModel) =>
								handleFailureLanguageConfigChange!(object, "failureMessage")
							}
							highlightRequiredDetail={highlightRequiredDetail}
							isValidationRequired={true}
						/>
						{IsAudioConfigurationAllowed(ivrFlowType) && (
							<MultiAudioUploadWidget
								title={strings.failureAudioFileLabel}
								widgetLanguageDetail={failureAudioFile as AudioFileModel[]}
								selectedLanguageList={selectedLanguageList}
								partitionKey={partitionKey}
								rowKey={rowKey}
								handleAudioFileDetailChange={(object: AudioFileModel) =>
									handleAudioFileChange!(object, "failureAudioFile")
								}
							/>
						)}
					</>
				)}
		</Grid>
	);
};
